import React from 'react';

import { TeamSong } from '@/graphql';
import { generateAlbumGradient } from '@/helpers/albumGradient';
import { classNames } from '@/helpers/classNames';
import { getAcronym } from '@/helpers/textTransform';
import constants from '@/theme/exportedVariables.module.scss';
import { Capacitor } from '@capacitor/core';
import { useMediaQuery } from '@material-ui/core';

import { Loader } from '../shared/Loader';
import { getRotationStatusClassName } from '../shared/RotationStatusLabel';
import styles from './SmallSongCard.module.scss';

const isCapacitor = Capacitor.isNativePlatform();

export type SmallSongCardSong = Pick<
	TeamSong,
	| 'name'
	| 'artist'
	| 'artworkUrl'
	| 'defaultKey'
	| 'recommendedKey'
	| 'recordedKey'
> & {
	rotationStatus?: TeamSong['rotationStatus'];
};

type OverrideFields = {
	defaultKey?: string | null;
};

export interface SmallSongCardProps {
	song: SmallSongCardSong;
	className?: string;
	coverImageClassName?: string;
	onClick?: () => void;
	isLoading?: boolean;
	displayRotationStatus?: boolean;
	displayKey?: boolean;
	onKeyClick?: () => void;
	removed?: boolean;
	inline?: boolean;
	score?: number | null;
	override?: OverrideFields;
}

export const SmallSongCard: React.FC<SmallSongCardProps> = ({
	song: rawSong,
	className,
	coverImageClassName,
	onClick,
	isLoading,
	displayRotationStatus,
	displayKey,
	onKeyClick,
	removed,
	inline = false,
	score,
	override,
}) => {
	const song = {
		...rawSong,
		...override,
	};

	const isTablet = useMediaQuery(`(min-width: ${constants.sizeTablet}`);

	const handleOnClick = () => {
		if (onClick) {
			return onClick();
		}
	};

	const songCardClasses = classNames([
		styles['song-card'],
		!isCapacitor && !removed && !isLoading && !!onClick && styles['hover'],
		removed && styles['removed'],
		inline && styles['inline'],
		className,
	]);

	const albumGradient =
		!isLoading && !song.artworkUrl
			? generateAlbumGradient(song.name)
			: null;

	return (
		<div className={songCardClasses} onClick={handleOnClick}>
			<div className={styles['clickable']}>
				<div
					className={classNames([
						styles['cover-image'],
						isLoading && styles['loading'],
						coverImageClassName,
					])}
					style={albumGradient ? albumGradient.style : undefined}
				>
					{isLoading ? (
						<Loader size={30} />
					) : song.artworkUrl ? (
						<img src={song.artworkUrl} />
					) : (
						<span
							style={
								albumGradient
									? { color: albumGradient.textColor }
									: undefined
							}
						>
							{getAcronym(song.name)}
						</span>
					)}
					<div
						className={classNames([
							displayRotationStatus &&
								song.rotationStatus &&
								styles['rotation'],
							getRotationStatusClassName(song.rotationStatus),
						])}
					/>
				</div>
				<div className={styles['title']}>
					{isLoading ? 'Loading ...' : song.name}
				</div>
			</div>

			{isLoading ? null : (
				<div className={styles['additional-items']}>
					{displayKey && (
						<div
							className={classNames([
								styles['key'],
								!isCapacitor &&
									onKeyClick &&
									styles['clickable-key'],
							])}
							onClick={
								onKeyClick
									? (e) => {
											e.stopPropagation();
											onKeyClick();
									  }
									: undefined
							}
						>
							{song.defaultKey ||
								song.recommendedKey ||
								song.recordedKey ||
								'-'}
						</div>
					)}
					{score !== undefined && (
						<div
							className={classNames([
								styles['score'],
								score === null && styles['score-none'],
							])}
						>
							{score === null ? '—' : score}
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export const SmallSongCardList: React.FC<{
	className?: string;
	wrap?: boolean;
}> = ({ children, className, wrap = true }) => (
	<div
		className={classNames([
			styles['song-card-list'],
			wrap && styles['wrap'],
			className,
		])}
	>
		{children}
	</div>
);
